import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { tasks as tasksApi, projects as projectsApi } from '../lib/api.js'
import { statusColor, typeLabel, progressPercent } from './tasks.js'

// --- Styles ---

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const titleRow = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${space.xl};
`

const taskTitleStyle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  line-height: 1.2;
`

const statusBadge = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  padding: 4px 12px;
  border-radius: 12px;
  white-space: nowrap;
`

const descStyle = css`
  font-size: ${font.size.base};
  color: ${colors.textSecondary};
  margin-top: ${space.sm};
  max-width: 600px;
  line-height: 1.6;
`

const metaRow = css`
  display: flex;
  gap: ${space.lg};
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-top: ${space.sm};
`

const sectionTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
`

const progressSection = css`
  margin-bottom: ${space.xl};
`

const progressBarLarge = css`
  height: 8px;
  background: ${colors.border};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: ${space.sm};
`

const progressFill = css`
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
`

const progressLabel = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
`

const inviteGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${space.md};
  margin-bottom: ${space.xl};
`

const inviteCard = css`
  ${card};
  padding: ${space.md};
`

const inviteEmail = css`
  font-weight: ${font.weight.medium};
  margin-bottom: ${space.xs};
`

const inviteStatus = css`
  font-size: ${font.size.xs};
  margin-bottom: ${space.sm};
`

const inviteLink = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  word-break: break-all;
  background: ${colors.bg};
  padding: ${space.xs} ${space.sm};
  border-radius: 4px;
  margin-top: ${space.sm};
`

const copyBtn = css`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  color: ${colors.textSecondary};
  cursor: pointer;
  margin-left: ${space.sm};
  &:hover { background: ${colors.bg}; }
`

const sessionListStyle = css`
  ${card};
  padding: 0;
  overflow: hidden;
`

const sessionRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const sessionTitle = css`
  flex: 1;
  font-size: ${font.size.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const sessionDone = css`
  font-size: ${font.size.xs};
  padding: 2px 6px;
  border-radius: 8px;
`

// --- Modal ---

const formOverlay = css`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const formCard = css`
  ${card};
  width: 480px;
  max-height: 90vh;
  overflow-y: auto;
`

const formTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.lg};
`

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

// --- Helpers ---

export function buildInviteUrl(projectId, token) {
  return `${location.origin}/#!/guest/${projectId}/${token}`
}

export function inviteStatusText(invite) {
  if (invite.accepted_at) return 'Accepted'
  const now = new Date()
  if (invite.expires_at && new Date(invite.expires_at) < now) return 'Expired'
  return 'Pending'
}

export function inviteStatusColor(invite) {
  if (invite.accepted_at) return colors.green
  const now = new Date()
  if (invite.expires_at && new Date(invite.expires_at) < now) return colors.textMuted
  return colors.orange
}

// --- Component ---

export function TaskDetailView() {
  let projectId = null
  let project = null
  let taskId = null
  let task = null
  let loading = true
  let showInviteForm = false
  let inviteData = { email: '' }
  let saving = false
  let copied = null // invite id that was just copied

  async function loadTask(pid, tid) {
    loading = true
    try {
      task = await tasksApi.get(pid, tid)
    } catch (err) {
      console.error('Failed to load task:', err)
      task = null
    } finally {
      loading = false
      m.redraw()
    }
  }

  function openInviteForm() {
    inviteData = { email: '' }
    showInviteForm = true
  }

  function closeInviteForm() {
    showInviteForm = false
    saving = false
  }

  async function submitInvite() {
    saving = true
    try {
      await tasksApi.createInvite(projectId, taskId, inviteData)
      closeInviteForm()
      await loadTask(projectId, taskId)
    } catch (err) {
      console.error('Failed to create invite:', err)
      saving = false
      m.redraw()
    }
  }

  async function closeTask() {
    try {
      await tasksApi.close(projectId, taskId)
      await loadTask(projectId, taskId)
    } catch (err) {
      console.error('Failed to close task:', err)
    }
  }

  function copyLink(invite) {
    const url = buildInviteUrl(projectId, invite.token)
    navigator.clipboard.writeText(url).then(() => {
      copied = invite.id
      m.redraw()
      setTimeout(() => { copied = null; m.redraw() }, 2000)
    })
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      taskId = vnode.attrs.taskId
      projectsApi.get(projectId).then(p => { project = p; m.redraw() }).catch(() => {})
      loadTask(projectId, taskId)
    },

    view(vnode) {
      projectId = vnode.attrs.projectId
      taskId = vnode.attrs.taskId

      if (loading) return m('div', 'Loading...')
      if (!task) return m('div', 'Task not found')

      const pct = progressPercent(task)
      const invites = task.invites || []
      const sessions = task.sessions || []

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}/tasks` }, 'Tasks'),
          ' / ',
          task.title,
        ]),

        m('div', { class: titleRow }, [
          m('div', [
            m('h1', { class: taskTitleStyle }, task.title),
            task.description
              ? m('p', { class: descStyle }, task.description)
              : null,
            m('div', { class: metaRow }, [
              m('span', typeLabel(task.type)),
              task.rubric ? m('span', `Rubric: ${task.rubric.title}`) : null,
              task.mask_pii ? m('span', 'PII masked') : null,
              task.expires_at
                ? m('span', `Expires ${new Date(task.expires_at).toLocaleDateString()}`)
                : null,
            ]),
          ]),
          m('div', { style: `display: flex; gap: ${space.sm};` }, [
            m('span', {
              class: statusBadge,
              style: `background: ${statusColor(task.status)}22; color: ${statusColor(task.status)};`,
            }, task.status),
            task.status === 'open'
              ? m('button', { class: btnSecondary, onclick: closeTask }, 'Close task')
              : null,
          ]),
        ]),

        // Progress
        m('div', { class: progressSection }, [
          m('h2', { class: sectionTitle }, 'Progress'),
          m('div', { class: progressBarLarge }, [
            m('div', {
              class: progressFill,
              style: `width: ${pct}%; background: ${statusColor(task.status)};`,
            }),
          ]),
          m('div', { class: progressLabel },
            `${task.completed || 0} of ${task.total || 0} sessions completed (${pct}%)`),
        ]),

        // Invites
        m('div', { style: `margin-bottom: ${space.xl};` }, [
          m('div', {
            style: `display: flex; justify-content: space-between; align-items: center; margin-bottom: ${space.md};`,
          }, [
            m('h2', { class: sectionTitle, style: 'margin-bottom: 0;' }, 'Invites'),
            task.status === 'open'
              ? m('button', { class: btnPrimary, onclick: openInviteForm }, 'Create invite')
              : null,
          ]),

          invites.length === 0
            ? m('div', {
                style: `padding: ${space.lg}; text-align: center; color: ${colors.textMuted};`,
              }, 'No invites yet. Create one to share this task.')
            : m('div', { class: inviteGrid },
                invites.map(inv =>
                  m('div', { key: inv.id, class: inviteCard }, [
                    inv.email
                      ? m('div', { class: inviteEmail }, inv.email)
                      : m('div', { class: inviteEmail }, 'Anonymous link'),
                    m('div', {
                      class: inviteStatus,
                      style: `color: ${inviteStatusColor(inv)};`,
                    }, inviteStatusText(inv)),
                    m('div', { class: inviteLink }, [
                      buildInviteUrl(projectId, inv.token),
                      m('button', {
                        class: copyBtn,
                        onclick: () => copyLink(inv),
                      }, copied === inv.id ? 'Copied!' : 'Copy'),
                    ]),
                  ])
                ),
              ),
        ]),

        // Sessions
        m('h2', { class: sectionTitle }, `Sessions (${sessions.length})`),
        sessions.length > 0
          ? m('div', { class: sessionListStyle },
              sessions.map(s =>
                m('div', { key: s.id, class: sessionRow }, [
                  m('span', { class: sessionTitle },
                    s.title || s.external_id || s.id.slice(0, 12)),
                  m('span', {
                    class: sessionDone,
                    style: s.is_done
                      ? `background: ${colors.green}22; color: ${colors.green};`
                      : `background: ${colors.border}; color: ${colors.textMuted};`,
                  }, s.is_done ? 'Done' : 'Pending'),
                ])
              ),
            )
          : null,

        // Invite modal
        showInviteForm
          ? m('div', { class: formOverlay, onclick: (e) => { if (e.target === e.currentTarget) closeInviteForm() } }, [
              m('div', { class: formCard }, [
                m('div', { class: formTitle }, 'Create invite'),

                m('div', { class: formRow }, [
                  m('label', { class: label }, 'Email (optional)'),
                  m('input', {
                    class: input,
                    type: 'email',
                    value: inviteData.email,
                    oninput: (e) => { inviteData.email = e.target.value },
                    placeholder: 'reviewer@example.com',
                    autofocus: true,
                  }),
                  m('div', {
                    style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; margin-top: ${space.xs};`,
                  }, 'Leave blank for a shareable anonymous link'),
                ]),

                m('div', { class: formActions }, [
                  m('button', { class: btnSecondary, onclick: closeInviteForm }, 'Cancel'),
                  m('button', {
                    class: btnPrimary,
                    disabled: saving,
                    onclick: submitInvite,
                  }, saving ? 'Creating...' : 'Create invite'),
                ]),
              ]),
            ])
          : null,
      ])
    },
  }
}
